import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Injector,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  TemplateRef,
  ViewContainerRef,
  ViewEncapsulation,
  computed,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { LiveAnnouncer } from '@angular/cdk/a11y';
import { TemplatePortal } from '@angular/cdk/portal';
import {
  SbDateTimeMode,
  SbDateTimeValueFormat,
  SbDateValueAs,
  boundDay,
  boundMessage,
  formatHint,
  formatSpokenDay,
  formatTimeDisplay,
  isoDate,
  outOfBounds,
  sameDay,
  textValue,
  timeValueText,
} from './sb-date-time-format';
import { SbDateParts, SbTimeParts } from './sb-date-time-parse';
import { SbDateTimeOverlay, coarsePointer, focusAfter, isRepeatClick, narrowViewport } from './sb-date-time-overlay';
import { SbDateTimePanelComponent } from './sb-date-time-panel.component';
import { SbDateTimeFieldComponent, SbFieldEdit } from './sb-date-time-field.component';
import {
  FieldState,
  SbTimeSegments,
  cleared,
  displayText,
  emptyState,
  fieldReading,
  fromValue,
  message,
  notice,
  partsShown,
  reading,
  sameValue,
  timeSegments,
  withParts,
  withTimeSegments,
} from './sb-date-time-segments';
import { SbPickView, SbQuickPickSet, computePicks, nowIn, todayIn } from './sb-quick-picks';

/** What the control tells its field after every edit, for validation and the message under it. */
export interface SbDateTimeState {
  /** The segments do not make a whole value yet (or cannot), while the value written is `''`/null. */
  textInvalid: boolean;
  /** What the field shows at rest, letters included (`10/1d/2026`). */
  text: string;
  /** The message for `.sb-field__error`, or ''. */
  error: string;
  /** `keepUnreadable`: the value is the one written from outside, not edited, or edited back to it. */
  held: boolean;
  /** A muted line under the field ("Year first: type the year, then - or /"), or ''. */
  notice: string;
}

/** What the trigger is called in each mode while the field is empty. */
const TRIGGER_NAME: Record<SbDateTimeMode, string> = { date: 'Choose date', time: 'Choose time', datetime: 'Choose date and time' };

const sameWritten = (a: unknown, b: unknown): boolean =>
  a instanceof Date && b instanceof Date ? a.getTime() === b.getTime() : a === b;

/**
 * One date, time or datetime field row: the segmented field (`app-sb-date-time-field`) and its label,
 * the trigger and clear buttons, and the popover. It turns what is typed or picked into the field's
 * value contract and reports it; the sb-input around it owns the form control.
 */
@Component({
  selector: 'app-sb-date-time-control',
  templateUrl: './sb-date-time-control.component.html',
  styleUrls: ['./sb-date-time-control.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [SbDateTimePanelComponent, SbDateTimeFieldComponent],
})
export class SbDateTimeControlComponent implements OnChanges, OnDestroy {
  readonly mode = input.required<SbDateTimeMode>();
  /** What the form holds. */
  readonly value = input<unknown>('');
  readonly valueAs = input<SbDateValueAs>('text');
  /** Datetime: the text a whole date and time is written in. */
  readonly valueFormat = input<SbDateTimeValueFormat>('YYYY-MM-DDTHH:mm');
  /** Time and datetime: a held value the field cannot read shows as it is, is valid and is kept until edited. */
  readonly keepUnreadable = input(false);
  readonly inputId = input.required<string>();
  readonly label = input('');
  readonly ariaLabel = input('');
  readonly placeholder = input('');
  readonly required = input(false);
  readonly disabled = input(false);
  readonly readonly = input(false);
  readonly min = input<Date | string | number | null>(null);
  readonly max = input<Date | string | number | null>(null);
  readonly quickPicks = input<SbQuickPickSet>('future');
  readonly timeZone = input<string | null>(null);
  /** Time mode: the page joins this time to a date field. */
  readonly joinsDate = input(false);
  /** Bumped by the field on every `writeValue`, so a value written twice still resets the field. */
  readonly revision = input(0);
  /** Ids of the field's description and error, for `aria-describedby`. */
  readonly describedBy = input('');
  /** Whether an error shows under the field. */
  readonly invalid = input(false);
  /** Pads the field for the help icon inside its end. */
  readonly helpInside = input(false);

  readonly valueChange = output<unknown>();
  readonly touched = output<void>();
  readonly stateChange = output<SbDateTimeState>();
  /** Time mode: a quick pick landed on another day than today (`YYYY-MM-DD`). */
  readonly dayPicked = output<string>();
  /** Focus left the field and its panel, or their form was submitted, with another value than the page last had from it, or wrote. */
  readonly entryDone = output<unknown>();

  private readonly injector = inject(Injector);
  private readonly viewContainerRef = inject(ViewContainerRef);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly announcer = inject(LiveAnnouncer);
  private readonly field = viewChild(SbDateTimeFieldComponent);
  private readonly triggerEl = viewChild<ElementRef<HTMLButtonElement>>('triggerEl');
  private readonly panelTpl = viewChild.required<TemplateRef<unknown>>('panelTpl');
  private readonly panel = viewChild(SbDateTimePanelComponent);

  readonly fieldState = signal<FieldState>(emptyState('date'));
  readonly isOpen = signal(false);
  readonly picks = signal<SbPickView[]>([]);
  readonly narrow = signal(false);
  /**
   * Counts each key, paste, undo or focus move in the field and each page write: the time columns
   * forget a minute or AM/PM waiting for the hour.
   */
  readonly fieldEdits = signal(0);
  /** The value written from outside, its state, and whether the field still holds it. */
  private heldValue: unknown = undefined;
  private heldState: FieldState | null = null;
  private held = false;
  private lastEmitted: unknown = undefined;
  /** The value the last entry ended on, or the page last wrote. */
  private settled: unknown = undefined;
  /**
   * The field was left, or Enter or Done committed it, and the entry has not changed since: an
   * unfinished entry says so, still when focus comes back to fix it. One typed since says nothing
   * between its digits until it is left or committed in turn.
   */
  private reported = false;
  /** The segment focus goes back to when the panel closes. */
  private lastSegment = -1;
  private readonly overlay = new SbDateTimeOverlay(this.injector);
  private refreshTimer: ReturnType<typeof setInterval> | null = null;
  /**
   * A form submitted with focus still here or the panel still open (a tap on a phone or assistive
   * technology need not move focus, nor press anything the panel hears): the entry ends first, as a
   * press on the button that moves focus ends it, before the form looks at its values, and the panel
   * goes, focus that was in it back to the field. So too an entry no leaving ended: a scroll that
   * closed the panel after focus dropped from it commits nothing. Heard on the way down, as the form's
   * own listener comes after.
   */
  private readonly onFormSubmit = (event: Event) => {
    const unended = this.isOpen() || this.host.nativeElement.contains(document.activeElement) || !sameWritten(this.lastEmitted, this.settled);
    if ((event.target as Node).contains(this.host.nativeElement) && unended) {
      this.commit();
      this.close(this.overlay.contains(document.activeElement));
    }
  };

  readonly fieldFocused = computed(() => this.field()?.focused() ?? false);
  readonly parts = computed(() => partsShown(this.fieldState()));
  /** The hour, minute and AM/PM the time columns show as chosen, each as the field holds it. */
  readonly timeParts = computed(() => timeSegments(this.fieldState()));
  readonly interactive = computed(() => !this.disabled() && !this.readonly());
  readonly panelId = computed(() => `${this.inputId()}-panel`);
  readonly hintId = computed(() => `${this.inputId()}-hint`);
  readonly labelId = computed(() => `${this.inputId()}-label`);
  readonly keptHintId = computed(() => `${this.inputId()}-kept`);
  readonly hint = computed(() => formatHint(this.mode(), this.interactive()));
  readonly describedByIds = computed(() => [this.hintId(), this.describedBy()].filter(Boolean).join(' '));
  readonly minDay = computed(() => boundDay(this.min() as Date | string | null));
  readonly maxDay = computed(() => boundDay(this.max() as Date | string | null));
  readonly hasValue = computed(() => {
    const state = this.fieldState();
    return state.kept !== null || state.segs.some((seg) => seg.value !== null || !!seg.typed || !!seg.refused);
  });
  /** Clear shows on optional, editable fields that hold something, as `showTimeClear` did. */
  readonly showClear = computed(() => this.interactive() && !this.required() && this.hasValue());
  /** The page's placeholder shows in the empty field while it is not focused. */
  readonly showPlaceholder = computed(() => !!this.placeholder() && !this.hasValue() && !this.fieldFocused());

  readonly triggerLabel = computed(() => {
    const read = reading(this.fieldState());
    const date = read.kind === 'complete' ? read.date : null;
    const time = read.kind === 'complete' ? read.time : null;
    if (this.mode() === 'time') {
      return time ? `Change time, ${formatTimeDisplay(time)}` : TRIGGER_NAME.time;
    }
    if (!date) {
      return TRIGGER_NAME[this.mode()];
    }
    return `Change ${this.mode() === 'datetime' ? 'date and time' : 'date'}, ${formatSpokenDay(date)}${time ? `, ${formatTimeDisplay(time)}` : ''}`;
  });

  readonly clearLabel = computed(() => `Clear ${this.label() || this.ariaLabel() || ''}`.trim());

  /** The hidden line that describes kept text: a phone has no Esc to bring it back, nor a Backspace that takes it. */
  readonly keptHint = coarsePointer()
    ? 'Saved as entered. Typing or a pick replaces it; the clear button empties it.'
    : 'Saved as entered. Typing replaces it; Escape brings it back.';

  constructor() {
    document.addEventListener('submit', this.onFormSubmit, true);
  }

  ngOnChanges(changes: SimpleChanges): void {
    const rewrite = (changes['value'] && this.value() !== this.lastEmitted)
      || (changes['revision'] && !changes['revision'].firstChange)
      || ((changes['mode'] || changes['valueAs'] || changes['keepUnreadable']) && !changes['mode']?.firstChange);
    if (rewrite || !this.heldState) {
      this.writeFromPage();
    }
    if ((changes['disabled'] || changes['readonly']) && !this.interactive()) {
      this.close(false);
    }
  }

  ngOnDestroy(): void {
    document.removeEventListener('submit', this.onFormSubmit, true);
    this.disposeOverlay();
    this.overlay.destroy();
  }

  // ─── The field ─────────────────────────────────────────────

  /** A key, paste, undo or focus move in the field. */
  onEdited(edit: SbFieldEdit): void {
    if (edit.changed) {
      this.reported = false;
    }
    // Focus leaving the field (into its panel) edits nothing; everything else in it counts.
    if (edit.state.focus >= 0) {
      this.lastSegment = edit.state.focus;
      this.fieldEdits.update((count) => count + 1);
    }
    if (edit.changed) {
      this.writeFromState(edit.state);
    } else {
      this.emitState();
    }
  }

  onKeydown(event: KeyboardEvent): void {
    // The Enter or Esc that ends an input method's composition is the input method's, not the field's.
    if (!this.interactive() || event.isComposing || event.keyCode === 229) {
      return;
    }
    if (event.key === 'Escape') {
      if (this.isOpen()) {
        event.preventDefault();
        event.stopPropagation();
        this.close(false);
        return;
      }
      if (this.field()?.restoreKept()) {
        event.preventDefault();
        event.stopPropagation();
      }
      return;
    }
    // Enter commits. The segments are no form input, so it submits no form, as the date and time
    // pickers they replace never did (their panel took the Enter).
    if (event.key === 'Enter') {
      this.commitState();
      if (this.isOpen()) {
        event.preventDefault();
        event.stopPropagation();
        this.close(false);
      }
    }
  }

  /** The second click of a double-click on the button is the same gesture as the first: it changes nothing. */
  onTriggerClick(event: MouseEvent): void {
    if (isRepeatClick(event)) {
      return;
    }
    if (this.isOpen()) {
      this.close(true);
      return;
    }
    this.open('keyboard');
  }

  /**
   * Commit and touched fire only when focus leaves both the field and its panel. Focus that drops
   * to nothing while the panel is open is the calendar redrawing its cells for another month, or
   * the panel closing; the overlay's focus and pointer listeners close the panel when focus or a
   * click really goes elsewhere.
   */
  onFocusOut(event: FocusEvent): void {
    const next = event.relatedTarget as Node | null;
    if (next ? this.host.nativeElement.contains(next) || this.overlay.contains(next) : this.isOpen()) {
      this.emitState();
      return;
    }
    this.commit();
    this.close(false);
  }

  /** Keeps focus where it is when the panel's empty areas are clicked. */
  onPanelMouseDown(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (!target.closest('button, [tabindex], input, [role="option"]')) {
      event.preventDefault();
    }
  }

  /** Focus goes back to the field (the trigger after touch): the clear button itself disappears. */
  clear(): void {
    this.apply(cleared(this.fieldState()));
    this.writeValue(this.valueAs() === 'date' ? null : '');
    if (this.isOpen()) {
      this.close(true);
      return;
    }
    this.refocus();
  }

  // ─── Panel ─────────────────────────────────────────────────

  open(by: 'pointer' | 'keyboard'): void {
    if (!this.interactive()) {
      return;
    }
    if (!this.isOpen()) {
      this.refreshPicks();
      this.narrow.set(narrowViewport());
      this.overlay.open(
        this.host.nativeElement.closest<HTMLElement>('.sb-field__control') ?? this.host.nativeElement,
        new TemplatePortal(this.panelTpl(), this.viewContainerRef),
        (target) => {
          if (!this.host.nativeElement.contains(target)) {
            this.commit();
            this.close(false);
          }
        },
        // A scroll that moves the field closes the panel; focus that was in it goes back to the field.
        () => this.close(this.overlay.contains(document.activeElement), true),
        (narrow) => this.narrow.set(narrow),
      );
      this.isOpen.set(true);
      this.refreshTimer = setInterval(() => this.refreshPicks(), 60000);
    }
    if (by === 'keyboard') {
      // The panel renders in this change detection pass; focus it once it is there.
      setTimeout(() => this.panel()?.focusInitial());
    }
  }

  /**
   * Closes the panel; `refocus` returns focus to the field (the trigger after touch), without
   * scrolling the page when the panel closed because the page scrolled. The panel goes while it
   * still counts as open: the focus Chrome drops from it as it goes ends no visit, so a pick, Done
   * or Esc leaves the field untouched and its undo history (and kept text) in place.
   */
  close(refocus: boolean, scrolled = false, to: 'last' | 'previous' = 'previous'): void {
    if (!this.isOpen()) {
      return;
    }
    this.disposeOverlay();
    this.isOpen.set(false);
    if (refocus) {
      this.refocus(scrolled, to);
    }
  }

  onDayChosen(choice: { date: SbDateParts; viaKeyboard: boolean }): void {
    this.apply(withParts(this.fieldState(), choice.date, null));
    if (this.mode() === 'date') {
      this.writeValue(this.valueAs() === 'date' ? this.pickedDate(choice.date) : isoDate(choice.date));
      this.announce(choice.date, null);
      this.close(true, false, 'last');
      return;
    }
    this.writeValue(this.valueFor(this.fieldState()));
    setTimeout(() => (choice.viaKeyboard ? this.panel()?.focusHourColumn() : this.panel()?.revealTime()));
  }

  /**
   * An hour, minute or AM/PM chosen in the columns sets that segment. A whole time with an empty
   * date takes today in the zone, as over kept text, which gives way to an empty field.
   */
  onTimeChosen(choice: Partial<SbTimeSegments>): void {
    const state = this.fieldState();
    let next = withTimeSegments(state, choice);
    const blankDate = this.mode() === 'datetime'
      && state.segs.every((seg) => !['month', 'day', 'year'].includes(seg.kind) || (seg.value === null && !seg.typed));
    if (blankDate && partsShown(next).time) {
      next = withParts(next, todayIn(this.timeZone()), null);
    }
    this.apply(next);
    this.writeValue(this.valueFor(this.fieldState()));
  }

  onPickChosen(view: SbPickView): void {
    const result = view.result;
    if (!result?.date) {
      return;
    }
    if (this.mode() === 'date') {
      this.onDayChosen({ date: result.date, viaKeyboard: false });
      return;
    }
    this.apply(withParts(this.fieldState(), this.mode() === 'time' ? null : result.date, result.time));
    this.writeValue(this.valueFor(this.fieldState()));
    if (this.mode() === 'time' && !sameDay(result.date, todayIn(this.timeZone()))) {
      this.dayPicked.emit(isoDate(result.date));
    }
    this.announce(this.mode() === 'time' ? null : result.date, result.time);
    this.close(true, false, 'last');
  }

  onToday(): void {
    const now = nowIn(this.timeZone());
    if (this.mode() === 'time') {
      this.onTimeChosen({ hour: now.time.hour % 12 || 12, minute: now.time.minute, period: now.time.hour >= 12 ? 1 : 0 });
      this.close(true, false, 'last');
      return;
    }
    this.onDayChosen({ date: now.date, viaKeyboard: false });
  }

  /** Done: the AM/PM rule, then back to the field. */
  onDone(): void {
    this.commitState();
    this.close(true);
  }

  /** Kept text comes back first: closing the panel can move focus out, which ends the edit. */
  onEscaped(): void {
    this.field()?.restoreKept();
    this.close(true);
  }

  onTabbedOut(direction: 'forward' | 'back'): void {
    const trigger = this.triggerEl()?.nativeElement;
    const panel = this.overlay.element;
    this.commit();
    this.close(false);
    if (direction === 'back') {
      if (trigger) {
        trigger.focus();
      } else {
        this.field()?.focus('last');
      }
      return;
    }
    if (trigger) {
      focusAfter(trigger, panel);
    }
  }

  // ─── Internals ─────────────────────────────────────────────

  /** The page wrote a value: shown, held until edited, everything else forgotten. */
  private writeFromPage(): void {
    this.lastEmitted = this.value();
    this.settled = this.value();
    this.heldValue = this.value();
    this.heldState = fromValue(this.value(), this.mode(), this.valueAs(), this.keepUnreadable());
    this.held = true;
    this.reported = false;
    this.fieldEdits.update((count) => count + 1);
    const field = this.field();
    if (field) {
      field.reset(this.heldState);
    } else {
      this.fieldState.set(this.heldState);
    }
    this.emitState();
  }

  /** A pick, a column choice or Clear: one history step in the field, and never the held value. */
  private apply(next: FieldState): void {
    this.held = false;
    this.reported = false;
    const field = this.field();
    if (field) {
      field.apply(next);
    } else {
      this.fieldState.set(next);
    }
  }

  /**
   * Writes the value the state stands for, in the field's contract (section 4 of the spec). A
   * state that shows the held value again (typed back, undone, kept text brought back) is that
   * value, byte for byte: a date field holding text it cannot show (`06/14/2030`) shows nothing
   * and keeps that text until something is typed or picked in it.
   */
  private writeFromState(state: FieldState): void {
    const held = this.heldState;
    if (held && sameValue(state, held)) {
      this.held = true;
      this.writeValue(this.heldValue);
      return;
    }
    this.held = false;
    this.writeValue(this.valueFor(state));
  }

  private valueFor(state: FieldState): unknown {
    const read = fieldReading(state);
    if (this.valueAs() === 'date') {
      return read.date ? new Date(read.date.year, read.date.month, read.date.day) : null;
    }
    if (this.mode() === 'time' && read.time) {
      return timeValueText(read.time);
    }
    return textValue(this.mode(), read, displayText(state), this.valueFormat());
  }

  private writeValue(value: unknown): void {
    if (!sameWritten(value, this.lastEmitted)) {
      this.lastEmitted = value;
      this.valueChange.emit(value);
    }
    this.emitState();
  }

  /** A calendar or quick pick with `valueAs="date"`: the day at the current local time, as Material's createDate did. */
  private pickedDate(date: SbDateParts): Date {
    const now = new Date();
    return new Date(date.year, date.month, date.day, now.getHours(), now.getMinutes(), now.getSeconds(), 0);
  }

  /** Enter, Done or leaving: the AM/PM rule fills an empty AM/PM, and the value follows. */
  private commitState(): void {
    this.reported = true;
    if (this.field()?.commit()) {
      this.writeFromState(this.fieldState());
      return;
    }
    this.emitState();
  }

  /** Focus left the field and its panel: the edit is done, and kept text it replaced stays replaced. */
  private commit(): void {
    this.commitState();
    this.field()?.endVisit();
    this.touched.emit();
    this.emitState();
    if (!sameWritten(this.lastEmitted, this.settled)) {
      this.settled = this.lastEmitted;
      this.entryDone.emit(this.settled);
    }
  }

  private emitState(): void {
    const state = this.fieldState();
    const read = reading(state);
    const focused = this.fieldFocused();
    // A held value a keepUnreadable field cannot read is left as it was saved, without a word.
    const held = this.held && this.keepUnreadable();
    const textInvalid = !this.held && read.kind !== 'empty' && read.kind !== 'complete';
    // A held value's own message is the field's (sbInput's value check); a refused paste still says why.
    const error = this.held ? (focused && state.rejected) || '' : message(state, focused, this.reported);
    // A whole date that breaks a valueAs="date" bound is announced while focus is still here; the
    // field shows the message (sbInputMin / sbInputMax).
    const side = read.kind === 'complete' && read.date && this.valueAs() === 'date' ? outOfBounds(read.date, this.minDay(), this.maxDay()) : null;
    if (side && focused) {
      this.announcer.announce(boundMessage(side, (side === 'min' ? this.minDay() : this.maxDay()) as SbDateParts), 'polite');
    }
    this.stateChange.emit({ textInvalid, text: displayText(state), error, held, notice: notice(state, focused) });
  }

  private refreshPicks(): void {
    const mode = this.mode();
    this.picks.set(computePicks(mode, this.quickPicks(), {
      now: Date.now(),
      zone: this.timeZone(),
      min: mode === 'time' ? null : this.minDay(),
      max: mode === 'time' ? null : this.maxDay(),
      joinsDate: this.joinsDate(),
    }));
  }

  private refocus(preventScroll = false, which: 'last' | 'previous' = 'previous'): void {
    const trigger = this.triggerEl()?.nativeElement;
    if (coarsePointer() && trigger) {
      trigger.focus({ preventScroll });
      return;
    }
    const field = this.field();
    if (which === 'previous' && this.lastSegment >= 0) {
      field?.focusAt(this.lastSegment, preventScroll);
      return;
    }
    field?.focus('last', preventScroll);
  }

  private announce(date: SbDateParts | null, time: SbTimeParts | null): void {
    const parts = [date ? formatSpokenDay(date) : '', time ? formatTimeDisplay(time) : ''].filter(Boolean);
    this.announcer.announce(`Selected ${parts.join(', ')}`, 'polite');
  }

  private disposeOverlay(): void {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
      this.refreshTimer = null;
    }
    this.overlay.close();
  }
}
